<script lang="ts">
  /**
   * 「まずこう考える」のステップ（problem-format.md §7）。最初は全部閉じていて、番号と見出しだけが見える。
   * 学習者が 1 つずつ開く。開閉の状態は保存しない（再読み込みで全部閉じる）。
   * 閉じている間は「計算量の予算」に印を付けない（考える前に答えの位置を教えない）。
   * 開いた後だけ、回数の見積もりの本文を枠で強調する。
   */
  import InlineNodes from '../lesson/InlineNodes.svelte';
  import type { ThinkStep } from './types';

  interface Props {
    steps: readonly ThinkStep[];
  }

  let { steps }: Props = $props();

  const uid = $props.id();
  let open = $state<Record<number, boolean>>({});
  const allOpen = $derived(steps.every((s) => open[s.index] === true));

  function toggle(index: number) {
    open[index] = open[index] !== true;
  }

  function openAll() {
    for (const s of steps) open[s.index] = true;
  }
</script>

<ol class="steps" data-testid="problem-steps">
  {#each steps as s (s.index)}
    {@const isOpen = open[s.index] === true}
    <li class="step" class:open={isOpen}>
      <button
        class="toggle"
        type="button"
        aria-expanded={isOpen}
        aria-controls="{uid}-step-{s.index}"
        data-testid="problem-step-toggle"
        data-index={s.index}
        onclick={() => toggle(s.index)}
      >
        <span class="chev" aria-hidden="true">{isOpen ? '▾' : '▸'}</span>
        <span class="num idx">{s.index}.</span>
        <span class="label"><InlineNodes nodes={s.label} /></span>
      </button>
      <div
        class="body"
        class:budget={s.budget}
        id="{uid}-step-{s.index}"
        hidden={!isOpen}
        data-testid="problem-step-body"
      >
        {#if s.budget}<span class="eyebrow budget-mark">回数で見積もる</span>{/if}
        <p><InlineNodes nodes={s.body} /></p>
      </div>
    </li>
  {/each}
</ol>
{#if steps.length > 1 && !allOpen}
  <div>
    <button
      class="btn btn-quiet btn-sm"
      type="button"
      data-testid="problem-steps-open-all"
      onclick={openAll}>全部開く</button
    >
  </div>
{/if}

<style>
  .steps {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .step {
    border: 1px solid var(--border-strong);
    border-radius: var(--radius-md);
    background: var(--surface);
    min-width: 0;
  }
  .toggle {
    display: flex;
    align-items: baseline;
    gap: var(--space-2);
    width: 100%;
    min-height: var(--tap-min);
    padding: var(--space-2) var(--space-3);
    border: 0;
    background: transparent;
    color: var(--text);
    font: inherit;
    font-weight: var(--fw-semibold);
    text-align: left;
    cursor: pointer;
    border-radius: var(--radius-md);
  }
  .toggle:hover {
    background: var(--canvas);
  }
  .chev {
    width: 1em;
    color: var(--text-soft);
  }
  .idx {
    color: var(--text-soft);
  }
  .body {
    padding: 0 var(--space-4) var(--space-3) calc(var(--space-3) + 2.5em);
  }
  .body p {
    margin: 0;
  }
  .body.budget {
    margin: 0 var(--space-3) var(--space-3);
    padding: var(--space-3) var(--space-4);
    border-left: 4px solid var(--accent);
    border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
    background: var(--accent-soft);
  }
  .budget-mark {
    display: block;
    color: var(--accent-soft-text);
    margin-bottom: var(--space-1);
  }
</style>
